export const EVENT_TIME_ZONE = 'America/Toronto';

const WALL_CLOCK_PATTERN = /^(\d{4})[/-](\d{2})[/-](\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,3})?)?)?$/;

const getTimeZoneOffset = (timestamp, timeZone) => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(timestamp));
  const values = Object.fromEntries(parts.map(part => [part.type, part.value]));
  const zonedAsUtc = Date.UTC(
    Number(values.year),
    Number(values.month) - 1,
    Number(values.day),
    Number(values.hour),
    Number(values.minute),
    Number(values.second)
  );

  return zonedAsUtc - timestamp;
};

export const parseTorontoWallClock = value => {
  const match = String(value || '').trim().match(WALL_CLOCK_PATTERN);

  if (!match) {
    return 0;
  }

  const wallClockAsUtc = Date.UTC(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    Number(match[4] || 0),
    Number(match[5] || 0),
    Number(match[6] || 0)
  );
  let timestamp = wallClockAsUtc - getTimeZoneOffset(wallClockAsUtc, EVENT_TIME_ZONE);

  // A second pass handles the hour on the other side of a daylight-saving transition.
  timestamp = wallClockAsUtc - getTimeZoneOffset(timestamp, EVENT_TIME_ZONE);
  return timestamp;
};

export const getCanonicalEventTimestamp = (event = {}) => {
  const isIssuedNotice = String(event.id || '').startsWith('issued-')
    || ['nouveau avis', 'ticket_created'].includes(String(event.type || '').toLowerCase());
  const issuedEventTime = isIssuedNotice
    ? String(event.eventTime || event.occurredAt || '').replace(/Z$/i, '')
    : '';
  const issuedTimestamp = parseTorontoWallClock(issuedEventTime);

  if (issuedTimestamp) {
    return issuedTimestamp;
  }

  const localDateTime = [event.date, event.time].filter(Boolean).join(' ');
  const localTimestamp = parseTorontoWallClock(localDateTime);

  if (localTimestamp) {
    return localTimestamp;
  }

  const explicitTimestamp = Number(event.timestamp);
  if (Number.isFinite(explicitTimestamp) && explicitTimestamp > 0) {
    return explicitTimestamp;
  }

  const parsedTimestamp = Date.parse(event.eventTime || event.occurredAt || '');
  return Number.isNaN(parsedTimestamp) ? 0 : parsedTimestamp;
};

export const formatEventTime = value => {
  const match = String(value || '').trim().match(/(?:^|[ T])(\d{1,2}):(\d{2})/);

  if (!match) {
    return '';
  }

  return `${String(Number(match[1])).padStart(2, '0')}:${match[2]}`;
};

export const formatEventDateTime = (date, timeOrDateTime) => {
  const dateMatch = String(date || timeOrDateTime || '')
    .trim()
    .match(/^(\d{4})[/-](\d{2})[/-](\d{2})/);
  const formattedTime = formatEventTime(timeOrDateTime);

  if (!dateMatch) {
    return formattedTime;
  }

  const formattedDate = `${dateMatch[1]}/${dateMatch[2]}/${dateMatch[3]}`;
  return formattedTime ? `${formattedDate} ${formattedTime}` : formattedDate;
};

const getTorontoDateTimeParts = timestamp => {
  const numericTimestamp = Number(timestamp);

  if (!Number.isFinite(numericTimestamp) || numericTimestamp <= 0) {
    return null;
  }

  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: EVENT_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(numericTimestamp));

  return Object.fromEntries(parts.map(part => [part.type, part.value]));
};

export const formatTorontoEventDate = timestamp => {
  const parts = getTorontoDateTimeParts(timestamp);
  return parts ? `${parts.year}-${parts.month}-${parts.day}` : '';
};

export const formatTorontoEventTime = timestamp => {
  const parts = getTorontoDateTimeParts(timestamp);
  return parts ? `${parts.hour}:${parts.minute}` : '';
};
